import { Button, Card } from '@kalos/ui';
import { type FC } from 'react';

import { ProposalProgressBar } from './ProposalProgressBar';

interface Step {
  key: string;
  label: string;
  content: React.ReactNode;
  nextDisabled?: boolean;
}

interface CustomerProposalWorkflowProps {
  step: string;
  onStepChange: (step: string) => void;
  steps: Step[];
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

export const CustomerProposalWorkflow: FC<CustomerProposalWorkflowProps> = ({
  step,
  onStepChange,
  steps,
  onSubmit,
  isSubmitting = false,
}) => {
  const currentStepIndex = steps.findIndex((s) => s.key === step);
  const currentStep = steps[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;

  // Helper function for cross-device scrolling
  const scrollToTop = () => {
    // Use multiple methods to ensure cross-device compatibility
    try {
      // Method 1: Modern smooth scrolling
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      // Method 2: Fallback for older browsers/devices
      window.scrollTo(0, 0);
    }

    // Method 3: Additional fallback for iOS Safari and other problematic browsers
    setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
  };

  const handleNext = () => {
    if (isLast && onSubmit) {
      // Last step - submit the form
      onSubmit();
    } else if (!isLast) {
      const nextStep = steps[currentStepIndex + 1];
      // Go to next step
      onStepChange(nextStep.key);

      // Only scroll to top when going to the Sign page
      if (nextStep.key === 'sign') {
        scrollToTop();
      }
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      onStepChange(steps[currentStepIndex - 1].key);
    }
    // No scroll on back navigation
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <ProposalProgressBar steps={steps} currentStepIndex={currentStepIndex} />
      <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col p-4 sm:p-6 lg:p-8">
        <Card className="flex flex-1 flex-col border-none bg-transparent p-0 shadow-none">
          {currentStep?.content}
        </Card>
        <Card className="mt-8 flex flex-row items-center justify-between border-t bg-white p-4 shadow-none">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isFirst || isSubmitting}
            className="bg-white"
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={handleNext}
            disabled={currentStep?.nextDisabled || isSubmitting}
            className="ml-auto"
          >
            {isSubmitting ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                Processing...
              </>
            ) : isLast ? (
              'Finish'
            ) : (
              <>Next: {steps[currentStepIndex + 1]?.label || 'Continue'}</>
            )}
          </Button>
        </Card>
      </div>
    </div>
  );
};
